import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { pinia } from './store/createPinia'
import { useShellStore } from './store'
import axios from 'axios'
import { i18n } from './i18n'
import { Auth, URLS, AxiosPlugin, sentryDns } from './dashboards'
import * as Sentry from '@sentry/vue'
import { Channel, plugins, utils, piniaStoreModules } from 'frontendcommon-3'
import 'frontendcommon-3/dist/frontendcommon-3.css'
import './scss/index.scss'
import NProgress from 'nprogress'
import { layoutUtils } from '@/utils'
import { useMicros, useShellStart } from '@/composables'
// Import Vuelidate for share dependency with rest of micros
import '@vuelidate/core'
import '@vuelidate/validators'
// Import polyfills
import './utils/polyfills'
import { detect } from 'detect-browser'

const sentryEnv = process.env.VUE_APP_SENTRY_ENV

const AXIOS_OPTIONS = {
  gatewayBaseUrl: URLS.GATEWAY_BASE_URL,
  accessToken: null,
  tokenUrl: URLS.TOKEN_URL,
  axios
}
const auth = new Auth()
const { i18nUtils: { loadLocaleMessages }, DateUtils: { dateTimeServerToUser } } = utils
NProgress.start()
NProgress.set(0.3)
let firstPage = window.location.pathname + window.location.search

const commonStore = piniaStoreModules.useCommon(pinia)

auth.getAuthData().then((authData) => {
  NProgress.set(0.6)
  const shellApp = createApp({
    render: () => <App />,
    setup () {
      const shellStore = useShellStore(pinia)
      const { updateAuthData } = useShellStart()
      return { updateAuthData, shellStore }
    },
    watch: {
      $route (to) {
        window.history.replaceState({ }, '', to.fullPath)
      }
    },
    created () {
      this.updateAuthData(authData)
      router.push(firstPage)
      NProgress.set(0.9)
    },
    async mounted () {
      const channel = new Channel()
      const micro = useMicros()
      channel.onAddServerError((event) => {
        this.shellStore.ADD_SERVER_ERROR(event.detail)
      })
      channel.onAddNotificationSuccess((event) => {
        this.shellStore.ADD_NOTIFICATION_SUCCESS(event.detail)
      })
      channel.onAddNotification((event) => {
        this.shellStore.ADD_NOTIFICATION(event.detail)
      })
      channel.onChangeLang((event) => {
        commonStore.changeLang(event.detail)
      })
      channel.onTokenUnauthorized(() => {
        auth.onTokenUnauthorized()
      })
      channel.onUpdateAuthDataEnded((event) => {
        this.shellStore.SET_TOKENS(event.detail.tokens)
        this.shellStore.SET_FARMACLOUD_MIGRATION_URL(event.detail.user.farmacloudMigrationUrl)
      })
      channel.onForbiddenRequest(() => {
        auth.onForbiddenRequest()
      })
      channel.onTokenRefresh(async ({ detail }) => {
        await auth.refreshToken(this.shellStore.tokens[detail.tokenType], detail.tokenType)
      })
      channel.onTokenRefreshEnd(async ({ detail }) => {
        this.shellStore.UPDATE_TOKENS(detail)
      })
      channel.onShellNewToken(async ({ detail }) => {
        this.shellStore.SET_FULL_LOADER(true)
        await auth.newToken(detail.tokenType)
        const authData = await auth.getAuthData(false, true)
        this.updateAuthData(authData)
        this.shellStore.SET_FULL_LOADER(false)
      })
      channel.onShellNewConfig(async () => {
        this.shellStore.SET_FULL_LOADER(true)
        const authData = await auth.getAuthData(false, true)
        this.updateAuthData(authData)
        this.shellStore.SET_FULL_LOADER(false)
      })
      channel.onShellFullLoader(async ({ detail }) => {
        this.shellStore.SET_FULL_LOADER(detail)
      })
      channel.onSidebarLoading(({ detail }) => {
        this.shellStore.SET_SIDEBAR_LOADING(detail)
      })
      channel.onUpdateSidebarItems(({ detail }) => {
        this.shellStore.SET_SIDEBAR(detail)
      })
      channel.onUpdateCustomization(({ detail }) => {
        this.shellStore.SET_CUSTOMIZATION(detail)
      })
      /*
       * Remove due to duplicate navigations
      */
      window.addEventListener('popstate', (event) => {
        router.push(window.location.pathname + window.location.search)
      })
      channel.onShellChangeMicro(({ detail }) => {
        if (typeof detail === 'object') {
          micro.changeMicroById(detail.microId)
        } else if (typeof detail === 'string') {
          micro.changeMicro(detail)
        }
      })
      channel.onShellLoadMicro(({ detail }) => {
        micro.loadMicro(detail, true)
      })
      channel.onShellBack(({ detail }) => {
        const isFisrtsPage = firstPage === window.location.pathname + window.location.search
        if (isFisrtsPage) {
          const defaultMicroId = router.resolve(window.location.pathname).meta.id
          const isSameMicro = defaultMicroId === this.shellStore.currentMicro.id
          if (isSameMicro) {
            micro.changeRoute(detail, true)
          } else {
            micro.changeMicro(detail, true)
          }
          firstPage = detail
        } else {
          window.history.back()
        }
      })
      channel.onShellUpdateSidebar(() => {
        router.push(window.location.pathname + window.location.search)
      })
      channel.onShellUpdateSidebarSize(({ detail }) => {
        this.shellStore.SET_SIDEBAR_SIZE(detail)
      })
      channel.onShellHideSidebar(() => {
        this.shellStore.SET_SHOW_SIDEBAR(false)
      })
      // open SettingsShell through events emmited by micros
      channel.onShellOpenSettingsModal(({ detail }) => {
        const value = detail == null ? true : detail
        this.shellStore.SET_SETTINGS_SHELL(value)
      })
      // open SettingsShell in a particular section through events emmited by micros with the itemId
      channel.onShellOpenSettingsModalItem(({ detail }) => {
        this.shellStore.SET_SETTINGS_SHELL(true)
        this.shellStore.SET_SETTINGS_ITEM_ID(detail)
      })
      // Breakpoints
      const updateBreakpoint = () => {
        const layoutData = layoutUtils.getBreakPoints()
        commonStore.updateBreakpoints(layoutData)
        Channel.emitResize(layoutData)
      }
      window.addEventListener('resize', updateBreakpoint)
      updateBreakpoint()
      commonStore.changeLang(authData.user.language)
      channel.onLog(() => {
        console.log(`app-shell:  Version ${VERSION} | Branch: ${BRANCH} | Build_date: ${dateTimeServerToUser(new Date(BUILT_DATE))} `)
      })
      NProgress.done()
    }
  })

  if (sentryEnv && sentryDns) {
    Sentry.init({
      app: shellApp,
      dsn: sentryDns,
      beforeSend (event) {
        const browser = detect()
        // ignore safari errors from versions 14 and lower
        if (browser && browser.name === 'safari' && Number(browser.version.split('.')[0]) <= 14) return null
        else return event
      },
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration()
      ],
      environment: sentryEnv,
      tracesSampleRate: 1.0
    })
  }

  const frontendAssetsUrl = URLS.FP_APPLICATIONS
  const envLocalesUrl = process.env.VUE_APP_TRANSLATIONS_ROUTE
  const i18nBaseLocalesUrl = `${frontendAssetsUrl}${envLocalesUrl}`
  const initialStateLang = authData.user.language || 'es' // commonStore.lang es es_es

  Promise.all([
    loadLocaleMessages(i18n, initialStateLang, i18nBaseLocalesUrl)
  ]).finally(() => {
    shellApp
      .use(pinia)
      .use(plugins.DesignSystemPlugin)
      .use(router)
      .use(i18n)
      .use(AxiosPlugin, AXIOS_OPTIONS)
      .mount('#shell-app-root')
  })
}).catch((e) => {
  if (e.message === 'Unauthorized') {
    auth.saveNextTo()
  }
})
